import { createAction, handleActions } from 'redux-actions';
import { useRedux } from 'util/hook/redux';
import { Dispatch } from 'redux';

import { oAuthWrapFetch, api } from 'util/api';
import { getGrModal } from 'models/events';


// 會有大小寫錯誤
// import {UserShowResource} from 'util/api/swaggerApi/data-contracts';

import { State as GlobalState } from './reducers';

interface JwtPayload {
	iss: string;
	iat: number;
	exp: number;
	nbf: number;
	jti: string;
	sub: number;
	prv: string;
	name: string;
	account: string;
}

/**
 * 用以將解析出的 JWT payload 存至 store
 */
export const getJwtPayload = createAction('GET_JWT_PAYLOAD', async (JwtToken: string) => {
	try {
		const parts = JwtToken.split('.');
		if (parts.length !== 3) {
			throw new Error('Invalid JWT format');
		}

		let payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/'); // Replace URL-safe base64 characters
		switch (
			payloadBase64.length % 4 // Add padding if required
		) {
			case 0:
				break; // No padding needed
			case 2:
				payloadBase64 += '==';
				break;
			case 3:
				payloadBase64 += '=';
				break;
			default:
				throw new Error('Invalid base64 string');
		}

		const payload = atob(payloadBase64);
		const result = JSON.parse(payload);
		return result;
	} catch (error) {
		console.log('get JWT token payload error!', error);
		return undefined;
	}
});

/**
 * 用以將 JWT token 存至 store
 */
export const getJwtToken = createAction('GET_JWT_TOKEN', async (JwtToken: string) => {
	try {
		return JwtToken;
	} catch (error) {
		console.log('get JWT token error!', error);

		return undefined;
	}
});

/**
 * 用以將首頁獲取的 user-agent 瀏覽器類型放入 store 讓其他頁可判斷打開網頁的瀏覽器裝置類型
 * (主要用以判斷在手機以瀏覽器0而非 app 打開網頁狀況下需開啟動態連結轉導到 app 商店 or 直接開啟 app)
 * 發成錯誤仍預設為 true , 因為若手機意外開啟桌機 modal 尺寸太大會遮擋, 且桌機就算開啟手機 modal 也頂多動態連結引導下載 app
 */
export const getBrowserType = createAction('GET_BROWSER_TYPE', async (isMobileBrowser: boolean) => {
	try {
		return isMobileBrowser;
	} catch (error) {
		console.log('get Browser Type error!', error);

		return true;
	}
});

/**
 * 用以將首頁獲取的 (webview 狀態帶 query string 由 app team 放入首頁) 裝置判斷字串 存至 store
 * 手機: 'ios', 'android' 桌機： ''
 */
export const getDevice = createAction('GET_DEVICE', async (deviceString: string) => {
	try {
		return deviceString;
	} catch (error) {
		console.log('get device type string payload error!', error);

		return '';
	}
});

/**
 * 打 token 用以向後端確認當前 token 是否具GR會員資格 (with bearer token)
 * v1WebUserGrMembershipList
 */
export const getIsMember = createAction('GET_IS_MEMBER', async () => {
	try {
		const { v1WebUserGrMembershipList } = api;
		const { data } = await v1WebUserGrMembershipList({ secure: true });

		// console.log('getIsMember isHere');
		// 這邊報錯是正確 後端表示文件跟實際都是 is_gr_member, 跑出 isGrMember 可能是 api library 問題？ 待釐清
		// console.log(data?.isGrMember);

		return {
			isMember: data?.isGrMember,
			expired: false,
		};
	} catch (error) {
		// 注意 proxy server 拿不到 response  或有錯都可能報 cors 所以錯誤印出來 cors type 不見得真的是 CORS 問題
		if (error instanceof Response) {
			// 不加型別檢查 error.status 會報錯
			console.log('getIsMember response error!', error);

			// 若為 401 response 則把 notExpired 改為  true
			if (error.status === 401) {
				// 這邊需注意這樣 return 代表此顆 token 是會員 只不過 token 效期已過
				return {
					isMember: true,
					expired: true,
				};
			}

			return {
				isMember: undefined,
				expired: undefined,
			};
		}

		// 其他非 Response 錯誤
		console.log('getIsMember other error:', error);
		return {
			isMember: undefined,
			expired: undefined,
		};
	}
});

/**
 * 打 token 用以 post 當前 token 是否具GR會員資格 (with bearer token)
 * V1WebUserGrMembershipCreate
 */
export const createMember = createAction('CREATE_MEMBER', async () => {
	try {
		const { v1WebUserGrMembershipCreate } = api;
		const data = await v1WebUserGrMembershipCreate({ secure: true });

		// console.log('createMember isHere');
		// console.log(data);

		return data.status;
	} catch (error) {
		// 注意 proxy server 拿不到 response  或有錯都可能報 cors 所以錯誤印出來 cors type 不見得真的是 CORS 問題
		console.log('createMember member error!', error);
		return undefined;
	}
});

/**
 * 打 token 用以向後端拿取此 token 會員資料 (with bearer token)
 * v2WebUserList
 */
export const getMemberData = createAction('GET_MEMBER_DATA', async () => {
	try {
		const { v2WebUserList } = api;
		const data = await v2WebUserList({ secure: true });

		// console.log('getMemberData isHere');
		// console.log(data.data);

		return data.data;
	} catch (error) {
		// 注意 proxy server 拿不到 response  或有錯都可能報 cors 所以錯誤印出來 cors type 不見得真的是 CORS 問題
		console.log('getMemberData error!', error);
		return undefined;
	}
});

/**
 * 打 token 用以 post 當前 token 是否具GR會員資格 (with bearer token)
 * V1WebUserGrMembershipCreate
 */
export const webAuthLogin = createAction('WEB_LOGIN', (codeStr: string, memberSeqStr: string) => async (dispatch: Dispatch) => {
	try {
		const { v2WebAuthLoginCreate } = api;
		const codeVerifierStr = localStorage.getItem('codeVerifier');
		const data = await v2WebAuthLoginCreate({
			authorizationCode: decodeURIComponent(codeStr),
			memberSeq: memberSeqStr,
			codeVerifier: codeVerifierStr || '',
		});
		if (data?.data && data?.data.accessToken) {
			const accessToken = data?.data.accessToken;
			localStorage.setItem('token', accessToken);
			dispatch(getJwtToken(accessToken)); // 將 JWT token 存入 store
			dispatch(getJwtPayload(accessToken)); // 解析並將 JWT payload 存入 store
			dispatch(getIsMember()) // 確認此 token 是否GR會員
			dispatch(getGrModal())
		}

		return null;
	} catch (error) {
		// 注意 proxy server 拿不到 response  或有錯都可能報 cors 所以錯誤印出來 cors type 不見得真的是 CORS 問題
		console.log('auth login error!', error);
		return undefined;
	}
});

/**
 * 登出刪除Token
 */
export const logout = createAction('WEB_LOGOUT', () => async (dispatch: Dispatch) => {
	localStorage.removeItem('token');
	dispatch(getJwtToken('')); // 將 JWT token 存入 store
	dispatch(getJwtPayload('')); // 解析並將 JWT payload 存入 store
	dispatch(getIsMember()) // 確認此 token 是否GR會員
	dispatch(getGrModal())
});

/**
 * 這支API未開發完成 =>
 * (因PM決定先不作 refresh, webview 若 token 過期或沒有 token 或 token 有問題一律導回 app login)
 *
 * 若 JWT token 逾期, POST 舊 token 以拿取新 token 並分別存至 localStorage 及 Store (with bearer token)
 * V2AppAuthRefreshTokenCreate
 */
// export const refreshToken = createAction('REFRESH_TOKEN', async () => {
// 	try {
// 		const { v2AppAuthRefreshTokenCreate } = api;
// 		const data = await v2AppAuthRefreshTokenCreate({ secure: true });

// 		console.log('refreshToken isHere');
// 		console.log(data);
// 		console.log(data.data?.accessToken);
// 		if (data.data?.accessToken) {
// 			localStorage.setItem('refreshToken', data.data?.accessToken);
// 		}

// 		return data.data?.accessToken;
// 	} catch (error) {
// 		// 注意 proxy server 拿不到 response  或有錯都可能報 cors 所以錯誤印出來 cors type 不見得真的是 CORS 問題
// 		console.log('refreshToken error!', error);
// 		return localStorage.getItem('token');
// 	}
// });

function RandomStr(length: any) {
	let result = '';
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
	for (let i = 0; i < length; i += 1) {
		const randomChar = chars.charAt(Math.floor(Math.random() * chars.length));
		result += randomChar;
	}
	return result;
}

async function SettingCodeData() {
	const codeVerifier = RandomStr(42);
	const codeChallenge = await SHA256(codeVerifier);
	localStorage.setItem('codeVerifier', codeVerifier);
	return codeChallenge;
}

async function SHA256(text: string) {
	const textAsBuffer = new TextEncoder().encode(text);
	const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
	const hashArray = Array.from(new Uint8Array(hashBuffer))
	const digest = hashArray.map((b: any) => b.toString(16).padStart(2, '0')).join('');
	return digest;
}

export const oAuthLogin = createAction('OAUTH_LOGIN', async () => {
	const codeChallenge = await SettingCodeData()
	try {
		const result = await oAuthWrapFetch(process.env.OAUTH_API_URL as string, {
			method: 'POST',
			body: JSON.stringify({
				grantType: 'authorization_code',
				clientId: process.env.CLIENT_ID,
				// codeChallenge: process.env.CLIENT_SECRET,
				codeChallenge,
				codeChallengeMethod: "S256",
				redirectUri: process.env.REDIRECT_URI
			}),
			headers: {
				'APP_ID': process.env.APP_ID,
			}
		});
		if (result) {
			const urlRegexStr = process.env.OAUTH_REGEX || '';
			const urlRegex = new RegExp(urlRegexStr);
			if (urlRegex.test(result.data.authRedirectUri)) {
				const authRedirectUri = encodeURI(result.data.authRedirectUri);
				window.location.href=decodeURI(authRedirectUri);
			}
		}
		return null;
	} catch (error) {
		return null;
	}
});

// 會有大小寫錯誤只好參考 api library 的 UserShowResource 自定義一個 camel case 版本 否則資料進不來
interface UserShowResource {
	id: number;
	memberSeq: string;
	name: string;
	account: string;
	email: string;
	birthday: string;
	sex: string;
	idNumber: string;
	memberState: string;
	grNumber: string;
}

export interface State {
	loading: boolean;
	loginLoading: boolean;
	JwtPayload: JwtPayload | undefined;
	JwtToken: string;
	isMobileBrowser: boolean;
	device: string;
	isGrMember: {
		isMember: boolean | undefined;
		expired: boolean | undefined;
	};
	createdMemberStateCode: number | undefined;
	memberData: undefined | UserShowResource;
}

export const defaultState: State = {
	loading: false,
	loginLoading: false,
	JwtPayload: undefined,
	JwtToken: '',
	isMobileBrowser: true,
	device: '',
	isGrMember: {
		isMember: undefined,
		expired: undefined,
	},
	createdMemberStateCode: undefined,
	memberData: undefined,
};

export const reducer = {
	login: handleActions<State, any>( // eslint-disable-line @typescript-eslint/no-explicit-any
		{
			GET_JWT_PAYLOAD_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_JWT_PAYLOAD_FULFILLED: (state, action) => ({
				...state,
				JwtPayload: action.payload,
				loading: false,
			}),

			GET_JWT_TOKEN_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_JWT_TOKEN_FULFILLED: (state, action) => ({
				...state,
				JwtToken: action.payload,
				loading: false,
			}),

			GET_BROWSER_TYPE_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_BROWSER_TYPE_FULFILLED: (state, action) => ({
				...state,
				isMobileBrowser: action.payload,
				loading: false,
			}),

			GET_DEVICE_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_DEVICE_FULFILLED: (state, action) => ({
				...state,
				device: action.payload,
				loading: false,
			}),

			GET_IS_MEMBER_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_IS_MEMBER_FULFILLED: (state, action) => ({
				...state,
				isGrMember: action.payload,
				loading: false,
				loginLoading: false,
			}),

			CREATE_MEMBER_PENDING: state => ({
				...state,
				loading: true,
			}),

			CREATE_MEMBER_FULFILLED: (state, action) => ({
				...state,
				createdMemberStateCode: action.payload,
				loading: false,
			}),

			GET_MEMBER_DATA_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_MEMBER_DATA_FULFILLED: (state, action) => ({
				...state,
				memberData: action.payload,
				loading: false,
			}),

			WEB_LOGIN_PENDING: state => ({
				...state,
				loginLoading: true,
			}),
			
			WEB_LOGIN_FULFILLED: state => ({
				...state,
			}),

			// REFRESH_TOKEN_PENDING: state => ({
			// 	...state,
			// 	loading: true,
			// }),

			// REFRESH_TOKEN_FULFILLED: (state, action) => ({
			// 	...state,
			// 	JwtToken: action.payload,
			// 	loading: false,
			// }),
		},
		defaultState,
	),
};

const loginActionsMap = {
	getJwtPayload,
	getJwtToken,
	getBrowserType,
	getDevice,
	getIsMember,
	createMember,
	getMemberData,
	oAuthLogin,
	webAuthLogin,
	logout,
	// refreshToken,
};

const mapHooksToState = (state: GlobalState) => ({
	loading: state.login.loading,
	loginLoading: state.login.loginLoading,
	JwtPayload: state.login.JwtPayload,
	JwtToken: state.login.JwtToken,
	isMobileBrowser: state.login.isMobileBrowser,
	device: state.login.device,
	isGrMember: state.login.isGrMember,
	createMemberStateCode: state.login.createdMemberStateCode,
	memberData: state.login.memberData,
});

type LoginSelector = ReturnType<typeof mapHooksToState>;
type LoginActionsMap = typeof loginActionsMap;

export const useLogin = () =>
	useRedux<LoginSelector, LoginActionsMap>(mapHooksToState, loginActionsMap);
